import {
  BacktestingRunType,
  BacktestingStatus,
  GetBacktestingRunsRequest,
  JobStatus,
} from "@shared/types";
import { selectJobsData } from "@shared/slices/jobs.slice";
import { getStore } from "@shared/common/utils";
import { sharedLib } from "@shared/services/shared.service";
import { skipToken } from "@reduxjs/toolkit/query";
import { difference, isEmpty } from "lodash";

const getJobData = (jobId?: string) => {
  const args = jobId ? { job_id: jobId } : undefined;
  const store = getStore();
  const jobsData = selectJobsData("getJobResultsJob", args)(store);
  const queryStatus = sharedLib.sharedApi.common.endpoints.getJobResults.select(
    args ?? skipToken
  )(store);
  return isEmpty(queryStatus.data) ? jobsData?.data : queryStatus.data;
};

export const getLiveBacktestingStatus = (backtestData: BacktestingRunType) => {
  const jobId =
    backtestData.status === BacktestingStatus.IN_PROGRESS
      ? backtestData?.job_id
      : undefined;
  const jobQueryData = getJobData(jobId);
  const liveStatus = jobQueryData?.status;
  return jobStatusToBacktestStatus(backtestData?.status, liveStatus);
};

export const jobStatusToBacktestStatus = (
  status?: BacktestingStatus,
  liveJobStatus?: JobStatus
) => {
  if (!liveJobStatus) return status;
  if (liveJobStatus === JobStatus.FINISHED) return BacktestingStatus.COMPLETED;
  else if (liveJobStatus === JobStatus.FAILED) return BacktestingStatus.FAILED;
  else if (liveJobStatus === JobStatus.IN_PROGRESS)
    return BacktestingStatus.IN_PROGRESS;
};

export const updateBacktestsData = (
  args: GetBacktestingRunsRequest | void,
  inProgressRunsData: BacktestingRunType[]
) => {
  return sharedLib.sharedApi.bots.util.updateQueryData(
    "getBacktestingRuns",
    args,
    (backtestsData) => {
      const backtests = backtestsData?.data;
      const idToIndex = backtests.reduce((acc, backtest, index) => {
        acc[backtest.id] = index;
        return acc;
      }, {} as Record<number, number>);
      const originalInProgressIds = backtests
        .filter((backtest) => backtest.status === BacktestingStatus.IN_PROGRESS)
        .map((backtest) => backtest.id);
      const setBotData = (backtest: BacktestingRunType) => {
        if (backtest.id in idToIndex) {
          const originalBacktest = backtests[idToIndex[backtest.id]];
          let updatedBacktest = { ...backtest };
          if (!updatedBacktest.original_status) {
            updatedBacktest.original_status = originalBacktest.status;
          }
          backtests[idToIndex[backtest.id]] = updatedBacktest;
        }
      };
      inProgressRunsData.forEach((backtest) => {
        setBotData(backtest);
      });
      const inProgressIds: number[] = inProgressRunsData.map(
        (backtest) => backtest.id
      );
      const missingInProgressIds = difference(
        originalInProgressIds,
        inProgressIds
      );
      missingInProgressIds.forEach((missingId) => {
        const missingBacktestData = backtests[idToIndex[missingId]];
        if (missingBacktestData) {
          setBotData({
            ...missingBacktestData,
            status: BacktestingStatus.FAILED,
          });
        }
      });
    }
  );
};
